import type { PermissionsLocalized } from 'src/api/main'
import { readLocalized, type LocalizedField } from 'src/utils'

import { ACTION_ORDER, type RolePermissionsMap } from './constants'
import type { MatrixRow } from './types'

const ALL_SUFFIX = 'all'

/**
 * A permission carrying optional localized `label`/`description` maps. The
 * generated SDK type omits these (the backend resolves them per-locale), so
 * the shape is layered on here for the role/catalog tables to read.
 */
export type LocalizedPermission = PermissionsLocalized & {
  label?: LocalizedField
  description?: LocalizedField
}

/**
 * True if a permission's code, localized label, or localized description
 * contains `query` (expected already trimmed + lower-cased). An empty query
 * matches every permission. Shared by the permissions catalog and the
 * third-party role matrix so the searchable fields stay in sync.
 */
export const permissionMatchesQuery = (
  permission: PermissionsLocalized,
  query: string,
  lang: string
): boolean => {
  if (!query) return true
  const localized = permission as LocalizedPermission

  return (
    permission.code.toLowerCase().includes(query) ||
    readLocalized(localized.label, lang).toLowerCase().includes(query) ||
    readLocalized(localized.description, lang).toLowerCase().includes(query)
  )
}

const capitalize = (str: string) =>
  str?.charAt(0)?.toUpperCase() + str?.slice(1)

const formatModuleName = (resource: string) =>
  resource.split('_').map(capitalize).join(' ')

export type PermissionLabel = {
  labelText: string
  hasAllSuffix: boolean
}

/** Parse a permission `code` (e.g. `view_leads:all`) into the cell label. */
export const getPermissionLabel = (code = ''): PermissionLabel => {
  const [codePart, ...suffixParts] = code?.split(':') ?? []
  const suffix = suffixParts?.join(':')
  const parts = codePart?.split('_') ?? []
  const action = parts[0] || ''
  const baseLabel = capitalize(action)

  if (!suffix) {
    return { labelText: baseLabel, hasAllSuffix: false }
  }
  if (suffix === ALL_SUFFIX) {
    return { labelText: `${baseLabel} All`, hasAllSuffix: true }
  }
  const formattedSuffix = suffix.split('_').map(capitalize).join(' ')

  return { labelText: `${baseLabel} ${formattedSuffix}`, hasAllSuffix: false }
}

/**
 * Group a flat list of permissions by their resource (the part of `code`
 * after the first underscore, with any `:scope` suffix stripped).
 *
 * Example: `view_leads:all` → resource `leads`.
 */
export const groupPermissionsByResource = (
  perms: readonly PermissionsLocalized[]
): Record<string, PermissionsLocalized[]> => {
  const grouped: Record<string, PermissionsLocalized[]> = {}
  for (const permission of perms) {
    const parts = permission.code.split('_')
    const resource = parts.slice(1).join('_').split(':')[0]
    if (!grouped[resource]) {
      grouped[resource] = []
    }
    grouped[resource].push(permission)
  }
  return grouped
}

type GroupedPermissionsOptions = {
  moduleOrder?: readonly string[]
  moduleLabels?: Record<string, string>
}

/**
 * Sorts grouped permissions: modules by `moduleOrder` (exact match first, then
 * prefix match), permissions within each module by `ACTION_ORDER`. Mirrors
 * WEB's `getGroupedPermissions`.
 */
export const getGroupedPermissions = (
  groupedPermissions: Record<string, PermissionsLocalized[]>,
  options?: GroupedPermissionsOptions
): MatrixRow[] => {
  const moduleOrder = options?.moduleOrder ?? []

  const getResourceOrder = (resource: string) => {
    for (const [index, orderedModule] of moduleOrder.entries()) {
      if (resource === orderedModule) {
        return { index, isPrefixMatch: false }
      }
      if (resource.startsWith(`${orderedModule}_`)) {
        return { index, isPrefixMatch: true }
      }
    }
    return undefined
  }

  return Object.entries(groupedPermissions)
    .map(([resource, permissions], originalIndex) => {
      const sortedPermissions = [...permissions].sort((a, b) => {
        const actionA = a?.code?.split('_')[0]
        const actionB = b?.code?.split('_')[0]
        const indexA = ACTION_ORDER.indexOf(actionA)
        const indexB = ACTION_ORDER.indexOf(actionB)

        if (indexA === -1 && indexB === -1) return 0
        if (indexA === -1) return 1
        if (indexB === -1) return -1

        return indexA - indexB
      })

      return {
        key: resource,
        module: options?.moduleLabels?.[resource] ?? formatModuleName(resource),
        permissions: sortedPermissions,
        originalIndex
      }
    })
    .sort((a, b) => {
      const moduleOrderA = getResourceOrder(a.key)
      const moduleOrderB = getResourceOrder(b.key)

      if (moduleOrderA && moduleOrderB) {
        if (moduleOrderA.index !== moduleOrderB.index) {
          return moduleOrderA.index - moduleOrderB.index
        }
        if (moduleOrderA.isPrefixMatch !== moduleOrderB.isPrefixMatch) {
          return (
            Number(moduleOrderA.isPrefixMatch) -
            Number(moduleOrderB.isPrefixMatch)
          )
        }
        return a.originalIndex - b.originalIndex
      }

      if (moduleOrderA) return -1
      if (moduleOrderB) return 1
      return a.originalIndex - b.originalIndex
    })
    .map(({ originalIndex: _omit, ...row }) => row)
}

/** Build the `{ [module]: number[] }` map of selected ids for a given role. */
export const getRoleInitialPermissions = (
  rolePermissions: { id: number }[] | undefined,
  groupedPermissions: Record<string, PermissionsLocalized[]>
): RolePermissionsMap => {
  const result: RolePermissionsMap = {}
  Object.entries(groupedPermissions)?.forEach(([module, permissions]) => {
    result[module] =
      permissions
        ?.filter((p) => rolePermissions?.some((rp) => rp?.id === p?.id))
        ?.map((p) => p?.id) || []
  })
  return result
}

export const areAllPermissionsSelected = (
  selected: number[],
  all: number[]
): boolean => all?.length > 0 && all?.every((id) => selected?.includes(id))
